import { useMemo, useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { CATEGORIES, PRODUCTS } from '../lib/products'
import ProductCard from '../components/ProductCard'
import { useI18n } from '../lib/i18n'
import { track } from '../lib/track'

// Consumer search terms that don't substring-match the product name.
// e.g. "pizza" is NOT a substring of "Pizzeria". Maps a typed word to extra
// needles we also test against each product's searchable text.
const SEARCH_SYNONYMS: Record<string, string[]> = {
  pizza: ['pizzeria', 'hospitality'],
  car: ['rental', 'rent'],
  doctor: ['clinic', 'medical', 'dentist'],
  dog: ['vet', 'pet', 'groom'],
  cat: ['vet', 'pet'],
  haircut: ['barber', 'hair', 'salon'],
  barber: ['barbershop'],
  food: ['restaurant', 'pizzeria', 'bakery', 'cafe', 'hospitality'],
  coffee: ['cafe', 'coffee-bar', 'hospitality'],
  lawyer: ['legal', 'notary'],
  rent: ['rental'],
  beauty: ['salon', 'spa', 'nail', 'tattoo'],
  call: ['voice', 'phone'],
  phone: ['voice'],
}

function searchTextFor(p: (typeof PRODUCTS)[number]): string {
  const cat = CATEGORIES.find((c) => c.slug === p.category)
  return `${p.name} ${p.tagline} ${p.slug} ${cat?.name ?? ''}`.toLowerCase()
}

export default function Catalog() {
  const { t } = useI18n()
  useEffect(() => {
    track('catalog_view')
  }, [])
  const [params, setParams] = useSearchParams()
  const initialCat = params.get('cat') ?? 'all'
  const [cat, setCat] = useState<string>(initialCat)
  const [query, setQuery] = useState('')

  useEffect(() => {
    if (cat === 'all') params.delete('cat')
    else params.set('cat', cat)
    setParams(params, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cat])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return PRODUCTS.filter((p) => {
      if (cat !== 'all' && p.category !== cat) return false
      if (!q) return true
      const hay = searchTextFor(p)
      if (hay.includes(q)) return true
      // Synonym fallback: any expansion of any typed word that matches.
      const needles = q.split(/\s+/).flatMap((w) => SEARCH_SYNONYMS[w] ?? [])
      return needles.some((n) => hay.includes(n))
    })
  }, [cat, query])

  return (
    <section className="section py-10">
      <header className="mb-8">
        <h1 className="text-3xl sm:text-5xl font-black tracking-tight text-[#071023]">{t('Catalog')}</h1>
        <p className="text-[#65728d] font-medium mt-2">{PRODUCTS.length} {t('products across')} {CATEGORIES.length} {t('categories — every one with a working demo.')}</p>
      </header>

      <FindBusiness />

      {/* Search */}
      <div className="mb-6">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('Search a product… (e.g. barbershop, restaurant, voice)')}
          className="w-full max-w-xl rounded-xl border border-[#dfe5ef] bg-white px-4 py-3 font-medium text-[#071023] placeholder-[#9ba6b8] shadow-sm focus:border-[#ffb21f] focus:outline-none"
          aria-label="Search catalog"
        />
      </div>

      {/* Category pills */}
      <div className="flex flex-wrap gap-2 mb-10 overflow-x-auto pb-2">
        <Pill active={cat === 'all'} onClick={() => setCat('all')} label={`${t('All')} (${PRODUCTS.length})`} />
        {CATEGORIES.map((c) => {
          const n = PRODUCTS.filter((p) => p.category === c.slug).length
          return (
            <Pill
              key={c.slug}
              active={cat === c.slug}
              onClick={() => setCat(c.slug)}
              label={`${c.icon} ${t(c.name)} (${n})`}
            />
          )
        })}
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-2xl border border-[#dfe5ef] bg-white p-8 text-center shadow-sm">
          <p className="font-medium text-[#65728d]">{t('No products match your search.')}</p>
        </div>
      ) : (
        <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filtered.map((p) => (
            <ProductCard key={p.slug} product={p} />
          ))}
        </div>
      )}
    </section>
  )
}

/**
 * "Find your business" — describe the business, AI picks the right products.
 * Webflow's "find your template" pattern, pointed at businesses instead.
 */
function FindBusiness() {
  const { t, lang } = useI18n()
  const [text, setText] = useState('')
  const [state, setState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle')
  const [matches, setMatches] = useState<{ slug: string; reason: string }[]>([])

  async function go(e: React.FormEvent) {
    e.preventDefault()
    if (text.trim().length < 8) return
    track('find_business')
    setState('loading')
    try {
      const res = await fetch('/api/find-business', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ describe: text.trim(), lang }),
      })
      const j = (await res.json()) as { matches?: { slug: string; reason: string }[] }
      if (!res.ok || !j.matches?.length) throw new Error('no match')
      setMatches(j.matches)
      setState('done')
    } catch {
      setState('error')
    }
  }

  const found = matches
    .map((m) => ({ ...m, product: PRODUCTS.find((p) => p.slug === m.slug) }))
    .filter((m) => m.product)

  return (
    <div className="mb-8 rounded-2xl border border-[#dfe5ef] bg-white p-6 shadow-sm">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-xl" aria-hidden>✨</span>
        <h2 className="text-lg font-black text-[#071023]">{t('Find your business')}</h2>
      </div>
      <p className="text-sm font-medium text-[#65728d] mb-4">
        {t('Describe the business — yours or one you want to pitch — and the AI picks the right product for it.')}
      </p>
      <form onSubmit={go} className="flex flex-col sm:flex-row gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t('e.g. "small bakery in Skopje, misses phone orders during rush hour"')}
          className="flex-1 rounded-xl border border-[#dfe5ef] bg-[#f7f9fd] px-4 py-3 font-medium text-[#071023] placeholder-[#9ba6b8] focus:border-[#ffb21f] focus:bg-white focus:outline-none"
          aria-label="Describe the business"
        />
        <button type="submit" disabled={state === 'loading'} className="shrink-0 rounded-xl bg-[#ffb21f] px-6 py-3 font-black text-[#211300] shadow-lg shadow-amber-300/40 transition hover:-translate-y-0.5 hover:bg-[#ffa40b] disabled:opacity-60">
          {state === 'loading' ? t('Matching…') : t('✨ Find it')}
        </button>
      </form>
      {state === 'error' && (
        <p className="mt-3 text-sm text-red-400">{t("Couldn't match that — try a few more words about what the business does.")}</p>
      )}
      {state === 'done' && found.length > 0 && (
        <div className="mt-5 grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {found.map((m, i) => (
            <div key={m.slug} className="relative">
              {i === 0 && (
                <span className="absolute -top-2.5 left-3 z-10 rounded-full bg-[#071023] px-2.5 py-1 text-[11px] font-black text-white shadow">{t('Best match')}</span>
              )}
              <ProductCard product={m.product!} />
              <p className="mt-1.5 text-xs font-medium text-[#65728d] px-1">{t(m.reason)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function Pill({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      onClick={onClick}
      className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-bold transition-colors border ${
        active
          ? 'bg-[#071023] text-white border-[#071023] shadow-sm'
          : 'bg-white text-[#65728d] border-[#dfe5ef] hover:border-[#ffb21f] hover:text-[#071023] shadow-sm'
      }`}
    >
      {label}
    </button>
  )
}
